import { LMap, LTileLayer, LControlScale } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { formatLatitude, formatLongitude } from '@/utils/coordinate';
import { 
  MAP_MODE, 
  getCurrentMapMode, 
  getDefaultOfflineLayers,
  OFFLINE_LAYER_CONFIGS,
  getOfflineTileUrlTemplate
} from '@/config/mapConfig';

export default {
  name: 'BaseMap',
  components: {
    LMap,
    LTileLayer,
    LControlScale
  },
  props: {
    initialZoom: {
      type: Number,
      default: 16
    },
    initialCenter: {
      type: Array,
      default: () => [23.13, 113.27] // 默认广州坐标
    },
    mapOptions: {
      type: Object,
      default: () => ({
        attributionControl: false,
        zoomControl: false
      })
    },
    customLayers: {
      type: Array,
      default: () => []
    },
    showFullscreen: {
      type: Boolean,
      default: true
    },
    showCoordinates: {
      type: Boolean,
      default: true
    },
    // 新增mapType参数，用于指定初始地图类型
    mapType: {
      type: String,
      default: 'normal', // 默认为天地图矢量图
      validator: value => ['normal', 'vector', 'satellite', 'img', 'terrain', 'ter', 'offline'].includes(value)
    },
    // 桥梁信息，用于获取桥梁位置
    bridgeInfo: {
      type: Object,
      default: null
    },
    // 🎯 是否显示测距工具
    showMeasureTool: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // 确保initialCenter是数组形式
    let initialCenter = this.initialCenter;
    if (initialCenter && !Array.isArray(initialCenter) && typeof initialCenter === 'object' && 'lat' in initialCenter && 'lng' in initialCenter) {
      initialCenter = [initialCenter.lat, initialCenter.lng];
    }
    
    // 根据mapType初始化空的图层数组，后续在created中根据mapType添加相应图层
    return {
      zoom: this.initialZoom,
      center: initialCenter,
      layers: [],
      isFullscreen: false,
      mousePosition: { lat: 0, lng: 0 },
      // 内部记录当前地图类型
      currentMapType: this.mapType,
      // 🗺️ 当前地图模式（天地图/离线地图）
      currentMapMode: getCurrentMapMode(),
      
      // 🎯 地图工具相关
      activeTool: '',
      showLocationDialog: false,
      coordinateFormat: 'decimal', // 'decimal' 或 'dms'
      decimalCoords: {
        lat: '',
        lng: '',
        isNorth: true, // true为N（北），false为S（南）
        isEast: true // true为E（东），false为W（西）
      },
      dmsCoords: {
        lat: {
          degrees: null,
          minutes: null,
          seconds: null,
          isNorth: true // true为N（北），false为S（南）
        },
        lng: {
          degrees: null,
          minutes: null,
          seconds: null,
          isEast: true // true为E（东），false为W（西）
        }
      },
      
      // 🎯 测距工具相关数据
      isMeasureMode: false,
      measureLines: [], // 存储测距线段
      measureDrawing: false, // 是否正在绘制测距线段
      measureCoordinates: [], // 当前绘制的测距线段坐标
      measureMousePosition: null, // 测距预览鼠标位置
      measureStartMarker: null, // 起点标记的Leaflet对象
      measurePreviewPolyline: null, // 预览线段的Leaflet对象
      measurePreviewTooltip: null // 预览提示的Leaflet对象
    };
  },
  
  computed: {
    // 🎯 获取桥梁位置坐标（优先从localStorage的selectedBridge读取，否则使用地图中心点）
    currentMapCenter() {
      let lat = 0, lng = 0;
      
      // 优先从桥梁信息prop获取位置
      if (this.bridgeInfo && this.bridgeInfo.latitude && this.bridgeInfo.longitude) {
        lat = this.bridgeInfo.latitude;
        lng = this.bridgeInfo.longitude;
        console.log('从桥梁信息获取位置:', lat, lng);
      } else {
        // 备选：从localStorage读取选中的桥梁位置
        try {
          const selectedBridge = localStorage.getItem('selectedBridge');
          if (selectedBridge) {
            const bridgeData = JSON.parse(selectedBridge);
            if (bridgeData.latitude && bridgeData.longitude) {
              lat = bridgeData.latitude;
              lng = bridgeData.longitude;
              console.log('从localStorage获取桥梁位置:', lat, lng);
            } else if (this.map) {
              // 如果桥梁数据不完整，使用地图中心点
              const center = this.map.getCenter();
              lat = center.lat;
              lng = center.lng;
              console.log('桥梁数据不完整，使用地图中心点:', lat, lng);
            }
          } else if (this.map) {
            // 如果没有选中桥梁，使用地图中心点
            const center = this.map.getCenter();
            lat = center.lat;
            lng = center.lng;
            console.log('无桥梁数据，使用地图中心点:', lat, lng);
          }
        } catch (error) {
          console.warn('读取桥梁位置失败，使用地图中心点:', error);
          if (this.map) {
            const center = this.map.getCenter();
            lat = center.lat;
            lng = center.lng;
          }
        }
      }
      
      // 十进制格式（保留6位小数）
      const decimal = {
        lat: Math.round(lat * 1000000) / 1000000,
        lng: Math.round(lng * 1000000) / 1000000
      };
      
      // 转换为度分秒格式
      const dms = {
        lat: this.decimalToDMS(lat, 'lat'),
        lng: this.decimalToDMS(lng, 'lng')
      };
      
      return { decimal, dms };
    },
    
    formatLongitude() {
      return this.mousePosition.lng ? formatLongitude(this.mousePosition.lng) : '-';
    },
    formatLatitude() {
      return this.mousePosition.lat ? formatLatitude(this.mousePosition.lat) : '-';
    },
    mapInstance() {
      return this.$refs.leafletMap ? this.$refs.leafletMap.mapObject : null;
    },
    
    // 🎯 判断当前页面是否为桥梁管理页面
    isBridgeManagementPage() {
      return this.$route && this.$route.path === '/bridge/management';
    }
  },
  watch: {
    // 监听mapType变化，动态切换地图类型
    mapType(newType, oldType) {
      if (newType !== oldType && newType !== this.currentMapType) {
        this.currentMapType = newType;
        this.changeMapType(newType);
      }
    }
  },
  created() {
    // 🗺️ 检查当前地图模式，决定使用天地图还是离线地图
    this.currentMapMode = getCurrentMapMode();
    
    if (this.currentMapMode === MAP_MODE.OFFLINE) {
      // 离线地图模式：使用离线图层
      this.loadOfflineLayers();
    } else {
      // 天地图模式：根据mapType初始化地图图层
      this.changeMapType(this.mapType);
    }
    
    // 合并自定义图层
    if (this.customLayers && this.customLayers.length > 0) {
      this.layers = [...this.layers, ...this.customLayers];
    }
    
    // 🗺️ 监听全局地图模式切换事件
    this.$root.$on('map-mode-changed', this.handleMapModeChange);
  },
  mounted() {
    // 添加全屏变化事件监听器
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange);
    
    // 添加ESC键监听以处理退出全屏
    document.addEventListener('keydown', this.handleKeyDown);
    
    // 添加窗口大小变化事件，确保地图大小正确更新
    window.addEventListener('resize', this.onWindowResize);
    
    // 组件挂载后，确保地图容器大小正确
    this.$nextTick(() => {
      if (this.mapInstance) {
        this.resizeMap();
      }
    });
  },
  beforeDestroy() {
    // 移除事件监听器
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.onWindowResize);
    
    // 🗺️ 移除地图模式切换事件监听
    this.$root.$off('map-mode-changed', this.handleMapModeChange);
  },
  methods: {
    onMapReady() {
      // 地图初始化完成后，确保尺寸正确
      if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
        const map = this.$refs.leafletMap.mapObject;
        
        // 立即调整一次地图大小
        map.invalidateSize();
        
        // 额外添加一个延时调整，确保在DOM渲染完成后执行
        setTimeout(() => {
          map.invalidateSize();
        }, 250);
      }
      
      this.$emit('map-ready', this.mapInstance);
    },
    centerUpdated(center) {
      // 确保center总是以数组形式传递出去
      if (center && Array.isArray(center)) {
        this.$emit('update:center', center);
      } else if (center && typeof center === 'object' && 'lat' in center && 'lng' in center) {
        this.$emit('update:center', center);
      }
    },
    zoomUpdated(zoom) {
      this.zoom = zoom;
      this.$emit('update:zoom', zoom);
    },
    onMouseMove(event) {
      this.mousePosition = event.latlng;
      this.$emit('mouse-move', event.latlng);
      
      // 🎯 如果在测距模式下，处理测距预览
      if (this.isMeasureMode && this.measureDrawing) {
        this.handleMeasureMouseMove(event.latlng);
      }
    },
    
    // 🎯 地图点击事件处理
    onMapClick(event) {
      // 如果在测距模式下，处理测距点击
      if (this.isMeasureMode) {
        this.handleMeasureClick(event.latlng);
      }
      // 发送点击事件给父组件
      this.$emit('map-click', event.latlng);
    },
    zoomIn() {
      this.requestZoom(1);
    },
    zoomOut() {
      this.requestZoom(-1);
    },
    // 缩放请求累积器：
    // Leaflet 在缩放动画进行中（_animatingZoom 为 true）会直接丢弃新的
    // zoomIn/zoomOut 请求（_tryAnimatedZoom 提前返回），导致快速连点缩放
    // 按钮时缩放"卡死"、迟迟到不了目标级别。这里把连续请求累积起来，
    // 每次动画结束后一次性跳到最终目标级别，保证快速点击也能顺畅缩放。
    requestZoom(delta) {
      if (!this.mapInstance) return;
      this._zoomRequestDelta = (this._zoomRequestDelta || 0) + delta;
      this.flushZoomRequest();
    },
    flushZoomRequest() {
      if (!this.mapInstance || this._zoomFlushBusy || !this._zoomRequestDelta) return;
      const map = this.mapInstance;
      // 单次最多应用 3 级（Leaflet zoomAnimationThreshold 默认 4），
      // 保证始终走缩放动画并触发 zoomend，避免跨级过大直接 resetView 而无 zoomend
      const d = Math.max(-3, Math.min(3, this._zoomRequestDelta));
      this._zoomRequestDelta -= d;
      const target = Math.max(
        map.getMinZoom(),
        Math.min(map.getMaxZoom(), map.getZoom() + d)
      );
      // 已在最大/最小边界：剩余请求没有意义，全部清零
      if (target === map.getZoom()) {
        this._zoomRequestDelta = 0;
        return;
      }
      this._zoomFlushBusy = true;
      map.setZoom(target);
      // 仅等 zoomend 释放 busy 并继续处理剩余累积请求。
      // 注意：不能同时监听 moveend——moveend 会因其他原因（如 zoom 属性回写、
      // resetView）提前触发，此时动画尚未真正结束，setZoom 会被 Leaflet 丢弃。
      map.once('zoomend', () => {
        this._zoomFlushBusy = false;
        this.flushZoomRequest();
      });
    },
    handleKeyDown(event) {
      // 监听ESC键以处理退出全屏
      if (event.key === 'Escape' && this.isFullscreen) {
        this.isFullscreen = false;
      }
    },
    toggleFullscreen() {
      try {
        // 获取当前全屏状态
        const isCurrentlyFullscreen = this.checkFullscreenStatus();
        
        // 如果当前就是全屏，则退出全屏
        if (isCurrentlyFullscreen) {
          this.exitFullscreen();
        } else {
          // 否则进入全屏
          this.enterFullscreen();
        }
        
        // 更新内部状态
        this.isFullscreen = !isCurrentlyFullscreen;
        
        // 刷新地图大小
        this.resizeMap();
      } catch (error) {
        console.error("全屏切换失败：", error);
      }
    },
    
    // 检查当前是否处于全屏状态
    checkFullscreenStatus() {
      return Boolean(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement || 
        document.msFullscreenElement
      );
    },
    
    // 进入全屏
    enterFullscreen() {
      const element = document.documentElement; // 使用整个文档元素获得类似F11的效果
      
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      } else {
        console.warn("当前浏览器不支持全屏API");
        return false;
      }
      
      return true;
    },
    
    // 退出全屏
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else {
        console.warn("当前浏览器不支持退出全屏API");
        return false;
      }
      
      return true;
    },
    
    // 调整地图大小以适应容器变化
    resizeMap() {
      if (this.mapInstance) {
        // 添加延时确保DOM已经更新
        setTimeout(() => {
          this.mapInstance.invalidateSize({ 
            animate: true,
            pan: true,
            debounceMoveend: true 
          });
          
          // 确保地图绑定的DOM元素存在并已渲染
          if (this.mapInstance._container && 
              this.mapInstance._container.offsetWidth > 0 && 
              this.mapInstance._container.offsetHeight > 0) {
            // 重新触发地图的移动结束事件，确保所有图层都能正确重绘
            this.mapInstance.fire('moveend');
          }
        }, 200);
      }
    },
    
    addLayer(layer) {
      this.layers.push(layer);
    },
    removeLayer(layerName) {
      const index = this.layers.findIndex(layer => layer.name === layerName);
      if (index !== -1) {
        this.layers.splice(index, 1);
      }
    },
    setLayerVisibility(layerName, visible) {
      const layer = this.layers.find(layer => layer.name === layerName);
      if (layer) {
        layer.visible = visible;
      }
    },
    getMap() {
      return this.mapInstance;
    },
    fitBounds(bounds, options) {
      if (this.mapInstance) {
        this.mapInstance.fitBounds(bounds, options);
      }
    },
    panTo(latlng, options) {
      if (this.mapInstance) {
        this.mapInstance.panTo(latlng, options);
      }
    },
    
    // 切换地图类型
    changeMapType(mapType, tiandituKey) {
      // 🗺️ 如果当前是离线模式，忽略天地图类型切换
      if (this.currentMapMode === MAP_MODE.OFFLINE) {
        console.log('当前为离线地图模式，忽略天地图类型切换');
        return;
      }
      
      // 确保有效的天地图密钥
      const tk = tiandituKey || '038937b9b91fd28203af58e3dd9b1396';
      
      // 清除所有基础底图（包括离线图层）
      this.layers = this.layers.filter(layer => 
        !['tianditu', 'tianditu-anno', 'tianditu-img', 'tianditu-img-anno', 'tianditu-ter', 'tianditu-ter-anno'].includes(layer.name) &&
        !layer.name.startsWith('offline-')
      );
      
      // 根据不同的地图类型添加对应的图层
      if (mapType === 'normal' || mapType === 'vector') {
        // 矢量底图
        this.addLayer({
          name: 'tianditu',
          url: `https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '&copy; <a href="http://www.tianditu.gov.cn/">天地图</a>',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'base',
          visible: true,
          options: {}
        });
        
        // 矢量注记
        this.addLayer({
          name: 'tianditu-anno',
          url: `https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {}
        });
      } 
      else if (mapType === 'satellite' || mapType === 'img') {
        // 卫星影像底图
        this.addLayer({
          name: 'tianditu-img',
          url: `https://t{s}.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '&copy; <a href="http://www.tianditu.gov.cn/">天地图</a>',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'base',
          visible: true,
          options: {}
        });
        
        // 卫星影像注记
        this.addLayer({
          name: 'tianditu-img-anno',
          url: `https://t{s}.tianditu.gov.cn/cia_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cia&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {}
        });
      }
      else if (mapType === 'terrain' || mapType === 'ter') {
        // 地形底图
        this.addLayer({
          name: 'tianditu-ter',
          url: `https://t{s}.tianditu.gov.cn/ter_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ter&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '&copy; <a href="http://www.tianditu.gov.cn/">天地图</a>',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'base',
          visible: true,
          options: {}
        });
        
        // 地形注记
        this.addLayer({
          name: 'tianditu-ter-anno',
          url: `https://t{s}.tianditu.gov.cn/cta_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cta&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`,
          attribution: '',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {}
        });
      }
    },
    
    // 🗺️ 加载离线地图图层
    loadOfflineLayers() {
      console.log('加载离线地图图层...');
      
      // 清除所有现有图层
      this.layers = this.layers.filter(layer => 
        !['tianditu', 'tianditu-anno', 'tianditu-img', 'tianditu-img-anno', 'tianditu-ter', 'tianditu-ter-anno'].includes(layer.name) &&
        !layer.name.startsWith('offline-')
      );
      
      // 获取默认离线图层配置
      const offlineLayers = getDefaultOfflineLayers();
      
      // 添加离线图层
      offlineLayers.forEach(layer => {
        if (layer) {
          this.addLayer(layer);
        }
      });
      
      console.log('离线地图图层加载完成，当前图层:', this.layers.map(l => l.name));
    },
    
    // 🗺️ 处理地图模式切换事件
    handleMapModeChange(newMode) {
      console.log('BaseMap收到地图模式切换事件:', newMode);
      
      if (newMode === this.currentMapMode) {
        return;
      }
      
      this.currentMapMode = newMode;
      
      if (newMode === MAP_MODE.OFFLINE) {
        // 切换到离线地图模式
        this.loadOfflineLayers();
      } else {
        // 切换回天地图模式
        this.changeMapType(this.currentMapType);
      }
      
      // 刷新地图
      this.$nextTick(() => {
        this.resizeMap();
      });
    },
    
    // 🗺️ 获取当前地图模式
    getMapMode() {
      return this.currentMapMode;
    },
    
    // 🗺️ 判断当前是否为离线地图模式
    isOfflineMode() {
      return this.currentMapMode === MAP_MODE.OFFLINE;
    },
    
    onFullscreenChange() {
      // 检查当前全屏状态
      const isCurrentlyFullscreen = this.checkFullscreenStatus();
      
      // 仅在状态不一致时更新
      if (this.isFullscreen !== isCurrentlyFullscreen) {
        this.isFullscreen = isCurrentlyFullscreen;
        
        // 刷新地图大小
        this.resizeMap();
        
        // 触发事件
        this.$emit('fullscreen-change', this.isFullscreen);
      }
    },
    
    // 处理窗口大小变化
    onWindowResize() {
      this.resizeMap();
    },
    
    // 🎯 地图工具方法
    // 切换工具
    toggleTool(tool) {
      if (this.activeTool === tool) {
        this.activeTool = '';
        // 🎯 如果是测距工具，退出并清理
        if (tool === 'measure') {
          this.exitMeasureModeAndClearAll();
        }
        this.$emit('tool-change', '');
      } else {
        // 🎯 如果之前有其他工具激活，先清理
        if (this.activeTool === 'measure') {
          this.exitMeasureModeAndClearAll();
        }
        this.activeTool = tool;
        // 🎯 如果是测距工具，启用测距模式
        if (tool === 'measure') {
          this.enableMeasureMode();
        }
        this.$emit('tool-change', tool);
      }
    },
    
    // 切换定位对话框
    toggleLocationDialog() {
      this.showLocationDialog = !this.showLocationDialog;
      if (this.showLocationDialog) {
        // 清空输入
        this.coordinateFormat = 'decimal';
        this.decimalCoords = { lat: '', lng: '', isNorth: true, isEast: true };
        this.dmsCoords = {
          lat: { degrees: null, minutes: null, seconds: null, isNorth: true },
          lng: { degrees: null, minutes: null, seconds: null, isEast: true }
        };
      }
    },
    
    // 定位到指定坐标
    gotoLocation() {
      try {
        let lat, lng;
        
        if (this.coordinateFormat === 'decimal') {
          // 十进制度数格式 - 普通文本输入，在此处进行验证
          const latStr = this.decimalCoords.lat;
          const lngStr = this.decimalCoords.lng;
          
          if (!latStr || !lngStr || latStr.trim() === '' || lngStr.trim() === '') {
            throw new Error('请输入完整的纬度和经度');
          }
          
          // 验证输入格式：只允许数字、小数点、负号
          const coordPattern = /^-?\d*\.?\d*$/;
          if (!coordPattern.test(latStr.trim()) || !coordPattern.test(lngStr.trim())) {
            throw new Error('坐标格式不正确，只能包含数字、小数点和负号');
          }
          
          lat = parseFloat(latStr);
          lng = parseFloat(lngStr);
          
          if (isNaN(lat) || isNaN(lng)) {
            throw new Error('坐标必须是有效数字');
          }
          
          // 根据switch状态调整符号
          if (!this.decimalCoords.isNorth) {
            lat = -Math.abs(lat); // 南纬为负
          } else {
            lat = Math.abs(lat); // 北纬为正
          }
          
          if (!this.decimalCoords.isEast) {
            lng = -Math.abs(lng); // 西经为负
          } else {
            lng = Math.abs(lng); // 东经为正
          }
        } else {
          // 度分秒格式
          lat = this.convertDMSToDecimal(this.dmsCoords.lat, 'lat');
          lng = this.convertDMSToDecimal(this.dmsCoords.lng, 'lng');
          
          if (lat === null || lng === null) {
            throw new Error('请输入完整的度分秒坐标');
          }
        }
        
        // 验证坐标范围
        if (lat < -90 || lat > 90) {
          throw new Error('纬度必须在-90到90之间');
        }
        
        if (lng < -180 || lng > 180) {
          throw new Error('经度必须在-180到180之间');
        }
        
        // 定位到指定坐标
        if (this.mapInstance) {
          this.mapInstance.setView([lat, lng], 15);
        }
        
        // 关闭对话框
        this.showLocationDialog = false;
        
        this.$message.success(`已定位到: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        
        // 发送定位事件
        this.$emit('goto-location', { lat, lng });
        
      } catch (error) {
        this.$message.error(`坐标解析失败: ${error.message}`);
      }
    },
    
    // 度分秒转十进制度数
    convertDMSToDecimal(dms, type) {
      const { degrees, minutes, seconds } = dms;
      
      if (degrees === null || degrees === '' || 
          minutes === null || minutes === '' || 
          seconds === null || seconds === '') {
        return null;
      }
      
      const deg = parseFloat(degrees);
      const min = parseFloat(minutes);
      const sec = parseFloat(seconds);
      
      if (isNaN(deg) || isNaN(min) || isNaN(sec)) {
        throw new Error('度分秒必须是有效数字');
      }
      
      if (min >= 60 || sec >= 60) {
        throw new Error('分和秒的值必须小于60');
      }
      
      let decimal = deg + min / 60 + sec / 3600;
      
      // 根据switch状态调整符号
      if (type === 'lat') {
        // 纬度：isNorth为false时为南纬（负数）
        if (!dms.isNorth) {
          decimal = -decimal;
        }
      } else {
        // 经度：isEast为false时为西经（负数）
        if (!dms.isEast) {
          decimal = -decimal;
        }
      }
      
      return decimal;
    },
    
    // 🎯 十进制度数转度分秒
    decimalToDMS(decimal, type) {
      const isNegative = decimal < 0;
      const absDecimal = Math.abs(decimal);
      
      const degrees = Math.floor(absDecimal);
      const minutesFloat = (absDecimal - degrees) * 60;
      const minutes = Math.floor(minutesFloat);
      const seconds = Math.round((minutesFloat - minutes) * 60 * 10) / 10; // 保留1位小数
      
      if (type === 'lat') {
        return {
          degrees,
          minutes,
          seconds,
          isNorth: !isNegative // 正数为北，负数为南
        };
      } else {
        return {
          degrees,
          minutes,
          seconds,
          isEast: !isNegative // 正数为东，负数为西
        };
      }
    },
    
    // ==================== 测距工具方法 ====================
    
    // 🎯 计算两点间的直线距离（米）
    calculateDistanceInMeters(point1, point2) {
      const R = 6371000; // 地球半径（米）
      const lat1 = point1[0] * Math.PI / 180;
      const lat2 = point2[0] * Math.PI / 180;
      const deltaLat = (point2[0] - point1[0]) * Math.PI / 180;
      const deltaLng = (point2[1] - point1[1]) * Math.PI / 180;
      
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      
      return R * c;
    },
    
    // 🎯 计算方向角（正北为0度，顺时针0-360度）
    calculateBearing(point1, point2) {
      const lat1 = point1[0] * Math.PI / 180;
      const lat2 = point2[0] * Math.PI / 180;
      const deltaLng = (point2[1] - point1[1]) * Math.PI / 180;
      
      const x = Math.sin(deltaLng) * Math.cos(lat2);
      const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
      
      let bearing = Math.atan2(x, y) * 180 / Math.PI;
      // 转换为0-360度
      bearing = (bearing + 360) % 360;
      
      return bearing;
    },
    
    // 🎯 启用测距模式
    enableMeasureMode() {
      this.isMeasureMode = true;
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      console.log('测距模式已启用');
      this.$message.info('测距模式已启用，请点击地图上的两个点进行测距');
    },
    
    // 🎯 禁用测距模式
    disableMeasureMode() {
      this.isMeasureMode = false;
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      
      // 清理Leaflet对象
      const map = this.mapInstance;
      if (this.measureStartMarker && map) {
        map.removeLayer(this.measureStartMarker);
        this.measureStartMarker = null;
      }
      if (this.measurePreviewPolyline && map) {
        map.removeLayer(this.measurePreviewPolyline);
        this.measurePreviewPolyline = null;
      }
      if (this.measurePreviewTooltip && map) {
        map.removeLayer(this.measurePreviewTooltip);
        this.measurePreviewTooltip = null;
      }
      
      console.log('测距模式已禁用');
    },
    
    // 🎯 退出测距模式并清理所有线段
    exitMeasureModeAndClearAll() {
      // 先禁用测距模式
      this.disableMeasureMode();
      
      // 清理所有测距线段
      this.clearAllMeasureLines();
      
      this.$message.info('已退出测距模式并清理所有线段');
      console.log('退出测距模式并清理所有线段完成');
    },
    
    // 🎯 清理所有测距线段
    clearAllMeasureLines() {
      const map = this.mapInstance;
      // 删除所有测距线段的Leaflet对象
      this.measureLines.forEach(line => {
        if (line.leafletPolyline && map) {
          map.removeLayer(line.leafletPolyline);
        }
        if (line.leafletMarker && map) {
          map.removeLayer(line.leafletMarker);
        }
        if (line.leafletDeleteMarker && map) {
          map.removeLayer(line.leafletDeleteMarker);
        }
      });
      
      // 清空测距线段数组
      this.measureLines = [];
      
      console.log('所有测距线段已清理');
    },
    
    // 🎯 处理测距点击
    handleMeasureClick(latlng) {
      const map = this.mapInstance;
      if (!map) return;
      
      // 确保坐标是纯JavaScript数组
      const point = [parseFloat(latlng.lat), parseFloat(latlng.lng)];
      
      if (!this.measureDrawing) {
        // 开始新的测距线段
        this.measureDrawing = true;
        this.measureCoordinates = [point];
        
        // 添加起点标记
        this.measureStartMarker = L.marker(point, {
          icon: this.getMeasureStartIcon()
        }).addTo(map);
        
        console.log('测距起点已设置:', point);
        this.$message.info('已设置起点，请点击终点完成测距');
      } else if (this.measureCoordinates.length === 1) {
        // 完成测距线段
        this.measureCoordinates.push(point);
        
        // 计算距离和方向角
        const distance = this.calculateDistanceInMeters(this.measureCoordinates[0], this.measureCoordinates[1]);
        const bearing = this.calculateBearing(this.measureCoordinates[0], this.measureCoordinates[1]);
        
        // 创建测距线段数据
        const measureLine = {
          id: Date.now(),
          points: JSON.parse(JSON.stringify(this.measureCoordinates)),
          distance: distance,
          bearing: bearing,
          distanceText: this.formatMeasureDistance(distance),
          bearingText: this.formatMeasureBearing(bearing)
        };
        
        // 保存测距线段
        this.measureLines.push(measureLine);
        
        // 添加线段到地图
        const polyline = L.polyline(measureLine.points, {
          color: '#ff4444',
          weight: 3,
          opacity: 0.8
        }).addTo(map);
        
        // 添加点击事件支持删除
        polyline.on('click', () => {
          this.deleteMeasureLineAndRestart(measureLine.id);
        });
        
        // 添加标签
        const midpoint = this.getMeasureLineMidpoint(measureLine.points);
        const marker = L.marker(midpoint, {
          icon: this.getMeasureLineIcon(measureLine)
        }).addTo(map);
        
        // 在第二个点位置添加删除标记
        const deleteMarker = L.marker(measureLine.points[1], {
          icon: this.getMeasureDeleteIcon(measureLine.id)
        }).addTo(map);
        
        // 添加删除标记的点击事件
        deleteMarker.on('click', (event) => {
          if (event.originalEvent) {
            event.originalEvent.stopPropagation();
          }
          this.deleteMeasureLineAndRestart(measureLine.id);
        });
        
        // 保存引用以便删除
        measureLine.leafletPolyline = polyline;
        measureLine.leafletMarker = marker;
        measureLine.leafletDeleteMarker = deleteMarker;
        
        // 清理起点标记和预览
        if (this.measureStartMarker) {
          map.removeLayer(this.measureStartMarker);
          this.measureStartMarker = null;
        }
        if (this.measurePreviewPolyline) {
          map.removeLayer(this.measurePreviewPolyline);
          this.measurePreviewPolyline = null;
        }
        if (this.measurePreviewTooltip) {
          map.removeLayer(this.measurePreviewTooltip);
          this.measurePreviewTooltip = null;
        }
        
        // 重置绘制状态
        this.measureDrawing = false;
        this.measureCoordinates = [];
        this.measureMousePosition = null;
        
        console.log('测距完成:', measureLine);
        this.$message.success(`测距完成: ${measureLine.distanceText}, ${measureLine.bearingText}`);
      }
    },
    
    // 🎯 处理测距鼠标移动（用于预览）
    handleMeasureMouseMove(latlng) {
      const map = this.mapInstance;
      if (!this.isMeasureMode || !this.measureDrawing || this.measureCoordinates.length !== 1 || !map) {
        return;
      }
      
      const startPoint = this.measureCoordinates[0];
      const currentPoint = [latlng.lat, latlng.lng];
      
      // 更新或创建预览线段
      if (this.measurePreviewPolyline) {
        this.measurePreviewPolyline.setLatLngs([startPoint, currentPoint]);
      } else {
        this.measurePreviewPolyline = L.polyline([startPoint, currentPoint], {
          color: '#ff4444',
          weight: 2,
          dashArray: '8, 6',
          opacity: 0.6
        }).addTo(map);
      }
      
      // 计算实时距离和方向角
      const distance = this.calculateDistanceInMeters(startPoint, currentPoint);
      const bearing = this.calculateBearing(startPoint, currentPoint);
      
      // 更新或创建预览提示
      if (this.measurePreviewTooltip) {
        map.removeLayer(this.measurePreviewTooltip);
      }
      this.measurePreviewTooltip = L.marker(currentPoint, {
        icon: this.getMeasurePreviewTooltipIcon(distance, bearing)
      }).addTo(map);
    },
    
    // 🎯 格式化距离显示
    formatMeasureDistance(distanceInMeters) {
      if (distanceInMeters < 1000) {
        return `${Math.round(distanceInMeters)}m`;
      } else {
        return `${(distanceInMeters / 1000).toFixed(2)}km`;
      }
    },
    
    // 🎯 格式化方向角显示
    formatMeasureBearing(bearing) {
      return `${Math.round(bearing)}°`;
    },
    
    // 🎯 获取测距线段中点坐标
    getMeasureLineMidpoint(points) {
      if (!points || points.length < 2) return [0, 0];
      const lat = (points[0][0] + points[1][0]) / 2;
      const lng = (points[0][1] + points[1][1]) / 2;
      return [lat, lng];
    },
    
    // 🎯 获取测距起点标记图标
    getMeasureStartIcon() {
      return L.divIcon({
        html: '<div class="measure-start-marker"></div>',
        className: 'measure-start-point',
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
    },
    
    // 🎯 获取测距预览提示图标
    getMeasurePreviewTooltipIcon(distance, bearing) {
      const distanceText = this.formatMeasureDistance(distance);
      const bearingText = this.formatMeasureBearing(bearing);
      
      return L.divIcon({
        html: `
          <div class="measure-preview-tooltip">
            <div class="measure-preview-distance">${distanceText}</div>
            <div class="measure-preview-bearing">${bearingText}</div>
          </div>
        `,
        className: 'measure-preview-tooltip-container',
        iconSize: [80, 40],
        iconAnchor: [40, 50]
      });
    },
    
    // 🎯 获取测距删除图标
    getMeasureDeleteIcon(lineId) {
      return L.divIcon({
        html: '<div class="measure-delete-marker"><i class="el-icon-close"></i></div>',
        className: `measure-delete-container-${lineId}`,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    },
    
    // 🎯 获取测距线段标签图标
    getMeasureLineIcon(line) {
      const lineAngle = this.calculateLineAngle(line.points[0], line.points[1]);
      
      return L.divIcon({
        html: `
          <div class="measure-label" style="transform: rotate(${lineAngle}deg);">
            <div class="measure-distance">${line.distanceText}</div>
            <div class="measure-bearing">${line.bearingText}</div>
          </div>
        `,
        className: `custom-measure-icon-${line.id}`,
        iconSize: [80, 40],
        iconAnchor: [40, 20]
      });
    },
    
    // 🎯 计算线段角度（用于文本旋转）
    calculateLineAngle(point1, point2) {
      const deltaX = point2[1] - point1[1];
      const deltaY = point1[0] - point2[0];
      let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      
      if (angle > 90) {
        angle -= 180;
      } else if (angle < -90) {
        angle += 180;
      }
      
      return angle;
    },
    
    // 🎯 删除测距线段
    deleteMeasureLine(lineId) {
      const map = this.mapInstance;
      const index = this.measureLines.findIndex(line => line.id === lineId);
      if (index !== -1) {
        const line = this.measureLines[index];
        
        // 从地图上移除Leaflet对象
        if (line.leafletPolyline && map) {
          map.removeLayer(line.leafletPolyline);
        }
        if (line.leafletMarker && map) {
          map.removeLayer(line.leafletMarker);
        }
        if (line.leafletDeleteMarker && map) {
          map.removeLayer(line.leafletDeleteMarker);
        }
        
        this.measureLines.splice(index, 1);
        this.$message.success('测距线段已删除');
        console.log('已删除测距线段:', lineId);
      }
    },
    
    // 🎯 删除测距线段并重新开始绘制
    deleteMeasureLineAndRestart(lineId) {
      // 删除指定的测距线段
      this.deleteMeasureLine(lineId);
      
      // 完全重置测距状态
      this.measureDrawing = false;
      this.measureCoordinates = [];
      this.measureMousePosition = null;
      
      const map = this.mapInstance;
      // 清理当前的预览对象
      if (this.measureStartMarker && map) {
        map.removeLayer(this.measureStartMarker);
        this.measureStartMarker = null;
      }
      if (this.measurePreviewPolyline && map) {
        map.removeLayer(this.measurePreviewPolyline);
        this.measurePreviewPolyline = null;
      }
      if (this.measurePreviewTooltip && map) {
        map.removeLayer(this.measurePreviewTooltip);
        this.measurePreviewTooltip = null;
      }
      
      this.$message.info('已删除线段，可重新开始测距');
      console.log('已删除测距线段并完全重置状态');
    }
  }
}; 